/**
 * Receives server messages related to the AI's internal thought process and tooling.
 * Centralizes the DOM manipulation and formatting of the "Thinking" UI.
 */
import aiLogger from './ai-logger';
import { IconService } from '../services/IconService';

export interface ToolProgressMeta {
    icon?: string;
    label?: string;
    category?: string;
    group_label?: string;
}

export interface ToolProgressPayload {
    type: 'reasoning_start' | 'reasoning_step' | 'reasoning_end' | 'tool_start' | 'tool_end' | 'tool_error' | 'ask_questions_pending' | 'ask_questions_timeout' | 'retry';
    tool?: string;
    description?: string;
    arguments?: any;
    iteration?: number;
    results_count?: number;
    message?: string;
    meta?: ToolProgressMeta;
    requestId?: string;
    questions?: any[];
    context?: any;
}

export interface AiThinkingManagerCallbacks {
    onBeforeRender?: () => void;
    onAfterRender: () => void;
    onAskQuestionsPending?: (payload: ToolProgressPayload) => void;
    onAskQuestionsTimeout?: () => void;
}

export class AiThinkingManager {
    private container: HTMLDivElement | null = null;
    private bodyElement: HTMLDivElement | null = null;
    private headerElement: HTMLDivElement | null = null;
    private titleElement: HTMLSpanElement | null = null;
    private toggleElement: HTMLSpanElement | null = null;

    private expanded: boolean = true;
    private activeStep: HTMLDivElement | null = null;
    
    private toolCallCount: number = 0;
    private thinkCount: number = 0;
    private askQuestionsUsed: boolean = false;
    
    constructor(private callbacks: AiThinkingManagerCallbacks) {}

    /**
     * Resets the state for a new streaming response
     */
    public reset(): void {
        if (this.container) {
            this.container.remove();
        }
        this.container = null;
        this.bodyElement = null;
        this.headerElement = null;
        this.titleElement = null;
        this.toggleElement = null;
        this.expanded = true;
        this.activeStep = null;
        this.toolCallCount = 0;
        this.thinkCount = 0;
        this.askQuestionsUsed = false;
    }

    /**
     * Injects the root UI container if it doesn't exist
     */
    public ensureContainer(parentElement: HTMLElement, insertBeforeElement: HTMLElement): void {
        if (this.container) return;

        this.container = document.createElement('div');
        this.container.className = 'thinking-steps';
        this.container.innerHTML = `
            <div class="thinking-steps-header">
                <span class="thinking-steps-icon">
                    ${IconService.render('athena-brain', 14)}
                </span>
                <span class="thinking-steps-title">Processando raciocínio...</span>
                <span class="thinking-steps-toggle">
                    <i data-lucide="chevron-down"></i>
                </span>
            </div>
            <div class="thinking-steps-body"></div>
        `;

        this.headerElement = this.container.querySelector('.thinking-steps-header');
        this.titleElement = this.container.querySelector('.thinking-steps-title');
        this.toggleElement = this.container.querySelector('.thinking-steps-toggle');
        this.bodyElement = this.container.querySelector('.thinking-steps-body');

        this.headerElement?.addEventListener('click', () => {
            this.expanded = !this.expanded;
            this.container!.classList.toggle('collapsed', !this.expanded);
            if (this.toggleElement) {
                const icon = this.toggleElement.querySelector('svg, i') as HTMLElement;
                if (icon) {
                    icon.style.transform = this.expanded ? 'rotate(0deg)' : 'rotate(-90deg)';
                    icon.style.transition = 'transform 0.2s';
                }
            }
            aiLogger.log('[Athena Debug] Thinking steps toggle:', this.expanded ? 'expandido' : 'colapsado');
        });

        if (this.callbacks.onBeforeRender) this.callbacks.onBeforeRender();
        parentElement.insertBefore(this.container, insertBeforeElement);
        IconService.refresh(this.container);
        this.callbacks.onAfterRender();
    }

    public handleProgress(payload: ToolProgressPayload, parentElement: HTMLElement, insertBeforeElement: HTMLElement): void {
        this.ensureContainer(parentElement, insertBeforeElement);

        if (payload.type === 'reasoning_start') {
            // Ignorado no front atual
        } else if (payload.type === 'reasoning_step') {
            const message = payload.message || '';
            aiLogger.log('[ATHENA-THINKING] reasoning_step recebido:', message.substring(0, 100));

            // Finaliza qualquer step ativo anterior antes de adicionar o pensamento
            if (this.activeStep) {
                this.finalizeStep(this.activeStep);
                this.activeStep = null;
            }

            // O backend (ToolOrchestratorService) não emite reasoning_end após o think,
            // portanto criamos o step já finalizado (isLoading=false) para que apareça
            // imediatamente. O texto do thought é exibido como sub-step legível.
            const thinkStep = this.addStep('Raciocínio', false, 'athena-brain');
            const substepsContainer = thinkStep.querySelector('.thinking-substeps') as HTMLDivElement;
            const list = thinkStep.querySelector('.thinking-substeps-list') as HTMLDivElement;
            if (list) {
                const sub = document.createElement('div');
                sub.className = 'thinking-substep';
                sub.innerHTML = `<span class="substep-text">${message}</span>`;
                list.appendChild(sub);
                // Expande automaticamente para o pensamento ser visível
                substepsContainer?.classList.remove('substeps-collapsed');
                thinkStep.classList.add('substeps-expanded', 'has-substeps');
            }
            // Não define activeStep — o think é atômico e já está completo
            this.thinkCount++;

        } else if (payload.type === 'reasoning_end') {
            if (this.activeStep) {
                this.finalizeStep(this.activeStep);
                this.activeStep = null;
            }

        } else if (payload.type === 'ask_questions_pending') {
            this.callbacks.onAskQuestionsPending?.(payload);

        } else if (payload.type === 'ask_questions_timeout') {
            this.callbacks.onAskQuestionsTimeout?.();

        } else if (payload.type === 'tool_start') {
            if (payload.tool === 'think') return;

            this.toolCallCount++;

            if (payload.tool === 'ask_questions') {
                this.askQuestionsUsed = true;
                if (!this.activeStep) {
                    this.activeStep = this.addStep('Aguardando usuário...', true, 'athena-question');
                }
                this.addSubStep('Aguardando resposta do usuário...', false, 'athena-question');
            } else {
                const groupLabel = payload.meta?.group_label || 'Processando...';
                const stepIcon = payload.meta?.icon || 'athena-settings';
                
                if (!this.activeStep) {
                    this.activeStep = this.addStep(groupLabel, true, stepIcon);
                } else {
                    // Atualiza o group_label do step ativo se ele mudar
                    const titleText = this.activeStep.querySelector('.thinking-step-text');
                    if (titleText && titleText.textContent !== groupLabel) {
                        // Finaliza o step anterior antes de criar um novo
                        this.finalizeStep(this.activeStep);
                        this.activeStep = this.addStep(groupLabel, true, stepIcon);
                    }
                }

                const subStepLabel = payload.meta?.label || payload.description || 'Executando ferramenta...';
                this.addSubStep(subStepLabel, false, stepIcon);
            }

        } else if (payload.type === 'tool_end') {
            if (payload.tool === 'think') return;

            const resultsCount = payload.results_count || 0;
            let endText: string;

            if (payload.tool === 'ask_questions') {
                endText = `Usuário respondeu - ${resultsCount} resposta${resultsCount !== 1 ? 's' : ''}`;
            } else {
                if (payload.meta?.label) {
                    endText = payload.meta.label;
                } else {
                    endText = payload.description || (resultsCount > 0 ? `${resultsCount} resultado${resultsCount > 1 ? 's' : ''} encontrado${resultsCount > 1 ? 's' : ''}` : 'Nenhum resultado');
                }
            }

            this.addSubStep(endText, true, 'athena-check');

        } else if (payload.type === 'tool_error') {
            const description = payload.description || 'Erro desconhecido';
            this.addSubStep(`Erro: ${description}`, true, 'athena-error');

        } else if (payload.type === 'retry') {
            // Reconexão automática do backend (StreamRetryHandler): dá feedback
            // visível em vez de deixar o usuário olhando o "Pensando..." parado.
            if (!this.activeStep) {
                this.activeStep = this.addStep('Reconectando...', true, 'athena-settings');
            }
            this.addSubStep(payload.description || 'Instabilidade na conexão — reconectando...', false);
        }
    }

    public handleSearchStatus(statusText: string, parentElement: HTMLElement, insertBeforeElement: HTMLElement): void {
        this.ensureContainer(parentElement, insertBeforeElement);
        if (!this.activeStep) {
            this.activeStep = this.addStep('Preparando...', true, 'athena-settings');
        }
        this.addSubStep(statusText, false);
    }

    public collapse(forceTitleUpdate: boolean = false): void {
        if (!this.container) return;
        if (!this.expanded && !forceTitleUpdate) return;

        if (this.activeStep) {
            this.finalizeStep(this.activeStep);
            this.activeStep = null;
        }

        if (this.titleElement) {
            if (this.askQuestionsUsed && this.toolCallCount === 1) {
                this.titleElement.textContent = 'Respondido';
            } else if (this.askQuestionsUsed) {
                this.titleElement.textContent = `Perguntou e buscou (${this.toolCallCount - 1})`;
            } else if (this.thinkCount > 0 && this.toolCallCount === 0) {
                this.titleElement.textContent = `Raciocínio (${this.thinkCount})`;
            } else if (this.thinkCount > 0) {
                this.titleElement.textContent = `Raciocínio e Ação (${this.toolCallCount})`;
            } else {
                this.titleElement.textContent = `Ação concluída (${this.toolCallCount})`;
            }
        }

        this.expanded = false;
        this.container.classList.add('collapsed');
        if (this.toggleElement) {
            const icon = this.toggleElement.querySelector('svg, i') as HTMLElement;
            if (icon) {
                icon.style.transform = 'rotate(-90deg)';
                icon.style.transition = 'transform 0.2s';
            }
        }
    }

    private addStep(text: string, isLoading: boolean = false, icon: string = 'athena-brain'): HTMLDivElement {
        if (!this.bodyElement) throw new Error("Body container not initialized");

        const step = document.createElement('div');
        step.className = 'thinking-step' + (isLoading ? ' is-loading' : '');
        step.style.flexWrap = 'wrap';

        const existingSteps = this.bodyElement.querySelectorAll('.thinking-step').length;
        const delay = existingSteps * 250;
        step.style.animationDelay = `${delay}ms`;
        step.style.opacity = '0';

        step.dataset.icon = icon;
        
        const iconHtml = isLoading 
            ? '<span class="thinking-spinner"></span>' 
            : IconService.render(icon, 14);

        step.innerHTML = `
            <span class="thinking-step-icon">${iconHtml}</span>
            <div class="thinking-step-content" style="display:flex; flex:1; align-items:flex-start; min-height: 18px; min-width: 0;">
                <span class="thinking-step-text" style="flex:1;">${text}</span>
                <span class="thinking-step-summary" style="display:none;"></span>
                <span class="thinking-step-toggle" style="opacity:0.5; transition: transform 0.2s; display:flex; align-items:center; justify-content:center; cursor:pointer; width: 24px; height: 18px; flex-shrink: 0; align-self: flex-start;">
                    <i data-lucide="chevron-down" style="width:14px;height:14px;"></i>
                </span>
            </div>
            <div class="thinking-substeps substeps-collapsed" style="width:100%; display:block;">
                <div class="thinking-substeps-list"></div>
            </div>
        `;

        IconService.refresh(step);

        step.addEventListener('click', () => {
            const substeps = step.querySelector('.thinking-substeps') as HTMLDivElement;
            if (!substeps) return;
            const list = substeps.querySelector('.thinking-substeps-list') as HTMLDivElement;
            if (!list || list.children.length === 0) return;
            
            const isExpanded = substeps.classList.toggle('substeps-collapsed') === false;
            step.classList.toggle('substeps-expanded', isExpanded);
            
            const toggleIcon = step.querySelector('.thinking-step-toggle');
            if (toggleIcon) {
                (toggleIcon as HTMLElement).style.transform = isExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
            }
        });

        this.bodyElement.appendChild(step);

        requestAnimationFrame(() => {
            step.style.opacity = '';
        });

        if (this.titleElement && this.expanded) {
            this.titleElement.textContent = text;
        }

        requestAnimationFrame(() => {
            step.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });

        return step;
    }

    private addSubStep(text: string, completed: boolean = false, iconPrefix?: string): void {
        if (!this.activeStep) {
            aiLogger.warn('[Athena] Sub-step sem reasoning ativo ignorado:', text);
            return;
        }

        let substepsContainer = this.activeStep.querySelector('.thinking-substeps') as HTMLDivElement;
        if (!substepsContainer) {
            substepsContainer = document.createElement('div');
            substepsContainer.className = 'thinking-substeps substeps-collapsed';
            substepsContainer.innerHTML = `<div class="thinking-substeps-list"></div>`;
            this.activeStep.appendChild(substepsContainer);
        }

        const list = substepsContainer.querySelector('.thinking-substeps-list') as HTMLDivElement;
        const previousSubs = list.querySelectorAll('.thinking-substep');
        
        if (previousSubs.length > 0) {
            const lastSub = previousSubs[previousSubs.length - 1] as HTMLDivElement;
            const lastSpinner = lastSub.querySelector('.substep-spinner');
            if (lastSpinner) {
                const icon = document.createElement('span');
                icon.className = 'substep-icon';
                icon.innerHTML = IconService.render('athena-check', 10, 'var(--color-positive)');
                lastSpinner.replaceWith(icon);
                IconService.refresh(lastSub);
            }
        }

        const delay = previousSubs.length * 250;
        const sub = document.createElement('div');
        sub.className = 'thinking-substep';
        sub.style.animationDelay = `${delay}ms`;

        if (completed) {
            const iconHtml = iconPrefix ? IconService.render(iconPrefix, 12, 'var(--color-positive)') : '';
            sub.innerHTML = `
                ${iconHtml}
                <span class="substep-text">${text}</span>
            `;
        } else {
            const spinIcon = iconPrefix ? IconService.render(iconPrefix, 12) : '<span class="substep-spinner"></span>';
            sub.innerHTML = `
                ${spinIcon}
                <span class="substep-text">${text}</span>
            `;
        }
        
        list.appendChild(sub);

        // Refresh AFTER insertion so Lucide can find the elements in the DOM
        if (iconPrefix || completed) {
            IconService.refresh(sub);
        }

        const summary = this.activeStep.querySelector('.thinking-step-summary') as HTMLSpanElement;
        if (summary) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = text;
            summary.textContent = tempDiv.textContent || text;
            this.activeStep.classList.add('has-substeps');
        }

        if (!this.expanded && this.titleElement) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = text;
            this.titleElement.textContent = tempDiv.textContent || text;
        }

        this.callbacks.onAfterRender();
    }

    private finalizeStep(step: HTMLDivElement): void {
        step.classList.remove('is-loading');
        
        const mainIcon = step.querySelector('.thinking-step-icon');
        if (mainIcon && step.dataset.icon) {
            mainIcon.innerHTML = IconService.render(step.dataset.icon, 14);
            IconService.refresh(mainIcon as HTMLElement);
        }

        const lastSpinner = step.querySelector('.thinking-substeps-list .thinking-substep:last-child .substep-spinner');
        if (lastSpinner) {
            const icon = document.createElement('span');
            icon.className = 'substep-icon';
            icon.innerHTML = IconService.render('athena-check', 10, 'var(--color-positive)');
            lastSpinner.replaceWith(icon);
            IconService.refresh(step);
        }
    }
}

